import React, { useState, useEffect } from 'react';
import Spinner from '../../components/layout/Spinner';
import FadeIn from '../../utils/fadein';
import {declineDemoBuyer, addBuyerDeclineReason, getBuyerDeclinedReasons} from '../../utils/api';
import { getCurrentUser, refresh } from '../../services/auth.service';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import { AlertTriangle, X, CheckCircle, AlertCircle } from 'lucide-react';

const BuyerDecline = ({ setShowDeclinedPopup, currentDemo, demos, setDemos, source, setShowReschedule}) => {
  const [buyerDeclinedReasons, setBuyerDeclinedReasons] = useState([]);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedDeclineReason, setSelectedDeclineReason] = useState(null);
  const [showAreYouSure, setShowAreYouSure] = useState(true);
  const [showNotification, setShowNotification] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  async function fetchBuyerDeclinedReasons() {
    let data = await getBuyerDeclinedReasons();
    if (data.success) {
      setBuyerDeclinedReasons(data.data);
    }
  }

  useEffect(() => {
    fetchBuyerDeclinedReasons();
  }, []);

  function closingClick() {
    setShowDeclinedPopup(false);
  }

  function notificationClosingClick() {
    setShowNotification(false);
    setShowDeclinedPopup(false);
  }

  async function handleSubmit(event, id) {
    event.preventDefault();

    if (selectedDeclineReason === null){
      alert("Please select a reason");
      return;
    }

    setIsProcessing(true);

    let res = await declineDemoBuyer(id, source, declineReason);

    if (res.success){
      await addBuyerDeclineReason(id, selectedDeclineReason, declineReason);
      setShowAreYouSure(!showAreYouSure);
      setShowNotification(true);
      currentDemo.demo_status = 'Declined';
      currentDemo.buyer_status = 'Declined';
    }

    else{
      await refresh();

      let res = await declineDemoBuyer(id, source, declineReason);

      if (res.success){
        await addBuyerDeclineReason(id, selectedDeclineReason, declineReason);
        setShowAreYouSure(!showAreYouSure);
        setShowNotification(true);
        currentDemo.demo_status = 'Declined';
        currentDemo.buyer_status = 'Declined';
      }
    }

    let _filtered = demos.filter(tmp => tmp.demo_id !== currentDemo.demo_id);
    setDemos(_filtered);
  }

  return (
    <>
    <FadeIn delay={120} duration={200}>
      <div className='overlay'></div>
      {showAreYouSure && (
        <div className='modal bg-white rounded-lg shadow-lg max-w-md w-full p-6'>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Cancel Demo</h2>
            </div>
            <button onClick={closingClick} className="text-gray-400 hover:text-gray-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-gray-700">
              Are you sure you want to decline the demo with <strong>{currentDemo.company_name}</strong>?
            </p>
          </div>
          
          <form onSubmit={(e) => handleSubmit(e, currentDemo.demo_id)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <select
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                onChange={(e) => setSelectedDeclineReason(e.target.value)}
              >
                <option value="0">Select a reason</option>
                {buyerDeclinedReasons.map((item, index) => (
                  <option key={index} value={item.decline_reason_id}>
                    {item.reason}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional notes
              </label>
              <textarea
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500"
                placeholder="Enter any additional comments..."
                rows={3}
              />
            </div>

            <div className="flex justify-end space-x-3">
              <button 
                type="button"
                onClick={closingClick}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              {!isProcessing && (
                <button
                  type="submit" 
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {source === 'Cancel' ? 'Confirm Cancellation' : 'Not a fit'}
                </button>
              )}
              {isProcessing && <Spinner />}
            </div>
          </form>
        </div>
      )}

      {showNotification && (
        <>
          <div className='overlay'></div>
          <div className='modal bg-white rounded-lg shadow-lg max-w-md w-full p-6'>
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Demo Cancelled</h2>
              </div>
              <button onClick={notificationClosingClick} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-700 mb-6">
              You have declined the demo with <span className="font-medium">{currentDemo.company_name}</span>.
              {source === 'Cancel' && (
                <span className="block mt-2">
                  We have informed them of your cancellation. Please check back often for new demo opportunities!
                </span>
              )}
            </p>

            <div className="flex justify-end">
              <button
                onClick={notificationClosingClick}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
      </FadeIn>
    </>
  );
};

export default BuyerDecline;